// Virtual number pad for amount entry. Replaces the OS soft keyboard on
// mobile so the layout never has to fight `visualViewport`. Keys: 0-9, '.', ⌫
// (backspace), and an optional `nextLabel` button that fires `onNext`.
//
// Props:
//   value:      current amount string (controlled)
//   onChange:   receives the new amount string after each key press
//   onNext?:    optional callback for the "下一步" confirm button (e.g. to blur
//               the field so the user can review the form before saving)
//   nextLabel?: label for the onNext button (default '下一步 →')
//   compact?:   shorter keys — used by AddTransactionModal (Phase 203),
//               where the numpad is PINNED to the bottom of the sheet and
//               the tall keys would push the amount display off-screen.
import React from 'react';

const KEYS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['.', '0', '⌫'],
];

export default function Numpad({ value, onChange, onNext, nextLabel = '下一步 →', compact = false }) {
  const handleKey = (key) => {
    if (key === '⌫') {
      onChange((value || '').slice(0, -1));
      return;
    }
    if (key === '.') {
      // Only one decimal point allowed.
      if ((value || '').includes('.')) return;
      // If empty, prepend a leading 0 so "0.5" stays valid.
      onChange((value || '') === '' ? '0.' : (value || '') + '.');
      return;
    }
    // Digit. Cap at 2 decimal places for currency.
    const next = (value || '') + key;
    const dotIdx = next.indexOf('.');
    if (dotIdx !== -1 && next.length - dotIdx - 1 > 2) return;
    onChange(next);
  };

  return (
    <div className="grid grid-cols-3 gap-1.5 mt-2 select-none" role="group" aria-label="數字鍵盤">
      {KEYS.flat().map((k) => (
        <button
          key={k}
          type="button"
          onClick={() => handleKey(k)}
          className={[
            'bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 active:bg-slate-300 dark:active:bg-slate-500 text-slate-900 dark:text-slate-100 text-xl font-medium rounded-lg transition-colors',
            compact ? 'py-1 min-h-[40px]' : 'py-3 min-h-[52px]',
          ].join(' ')}
          aria-label={k === '⌫' ? '刪除' : k === '.' ? '小數點' : `數字 ${k}`}
        >
          {k}
        </button>
      ))}
      {onNext && (
        <button
          type="button"
          onClick={onNext}
          className="col-span-3 bg-brand-600 hover:bg-brand-700 active:bg-brand-700 text-white text-sm font-semibold rounded-lg py-2.5 min-h-[44px] mt-1"
        >
          {nextLabel}
        </button>
      )}
    </div>
  );
}